import { forwardRef, type ElementType, type ComponentType, type ForwardedRef } from 'react';
import type { PolymorphicComponentPropsWithRef, IntrinsicElement } from '@ovpn/ui/core';
import styled from 'styled-components';
import isNil from 'lodash/isNil';

export type BoxProps = Partial<{
    minHeight: string | number;
    maxHeight: string | number;
    minWidth: string | number;
    maxWidth: string | number;
    center: boolean;
    inline: boolean;
    flex: boolean;
    wide: boolean;

    mb: string | number;
    mt: string | number;
    ml: string | number;
    mr: string | number;
    m: string | number;

    pb: string | number;
    pt: string | number;
    pl: string | number;
    pr: string | number;
    p: string | number;
}>;

export type StyledBoxProps = Partial<{
    $minHeight: string | number;
    $maxHeight: string | number;
    $minWidth: string | number;
    $maxWidth: string | number;
    $center: boolean;
    $inline: boolean;
    $flex: boolean;
    $wide: boolean;

    $mb: string | number;
    $mt: string | number;
    $ml: string | number;
    $mr: string | number;
    $m: string | number;

    $pb: string | number;
    $pt: string | number;
    $pl: string | number;
    $pr: string | number;
    $p: string | number;
}>;

export type BoxedComponentProps<Props, As extends ElementType> = PolymorphicComponentPropsWithRef<
    As,
    BoxProps & Props
>;

export type BoxedComponent<Props, As extends ElementType> = ComponentType<
    BoxedComponentProps<Props, As>
>;

export const defaultPx = (prop: string | number) => (typeof prop === 'number' ? `${prop}px` : prop);

export const propsToStyleMap = {
    $maxHeight: { name: 'max-height', apply: defaultPx },
    $minHeight: { name: 'min-height', apply: defaultPx },
    $maxWidth: { name: 'max-width', apply: defaultPx },
    $minWidth: { name: 'min-width', apply: defaultPx },
    $wide: { name: 'width', apply: () => '100%' },

    $mb: { name: 'margin-bottom', apply: defaultPx },
    $mr: { name: 'margin-right', apply: defaultPx },
    $ml: { name: 'margin-left', apply: defaultPx },
    $mt: { name: 'margin-top', apply: defaultPx },
    $m: { name: 'margin', apply: defaultPx },

    $pb: { name: 'padding-bottom', apply: defaultPx },
    $pr: { name: 'padding-right', apply: defaultPx },
    $pl: { name: 'padding-left', apply: defaultPx },
    $pt: { name: 'padding-top', apply: defaultPx },
    $p: { name: 'padding', apply: defaultPx },
};

export function selectCommonStyle(props: StyledBoxProps) {
    return Object.keys(propsToStyleMap)
        .filter(k => !isNil(props[k as keyof typeof props]))
        .map(k => {
            const { name, apply } = propsToStyleMap[k as keyof typeof propsToStyleMap];
            const value = (apply as (prop: unknown) => string)(props[k as keyof typeof props]);

            return `${name}: ${value};`;
        })
        .join('');
}

export function selectDisplayStyle({ $flex, $inline }: StyledBoxProps) {
    if ($flex) {
        return `display: ${$inline ? 'inline-flex' : 'flex'}`;
    }

    if ($inline) {
        return `display: ${$inline ? 'inline-block' : 'block'}`;
    }

    return '';
}

export function selectAlignmentStyle({ $flex, $center }: StyledBoxProps) {
    if ($center) {
        return `${$flex ? 'justify-content' : 'text-align'}: center;`;
    }

    return '';
}

const boxed = <Props, As extends ElementType>(Component: As | ComponentType<Props>) => {
    const StyledComponent = styled(Component)`
        ${selectAlignmentStyle};
        ${selectDisplayStyle};
        ${selectCommonStyle};
    ` as ComponentType<Props & StyledBoxProps>;

    const BoxedComponent = (
        {
            minHeight,
            maxHeight,
            minWidth,
            maxWidth,
            center,
            inline,
            flex,
            wide,
            mb,
            mt,
            ml,
            mr,
            m,
            pb,
            pt,
            pl,
            pr,
            p,
            as,
            ...props
        }: BoxedComponentProps<Props, As>,
        ref?: ForwardedRef<IntrinsicElement<As>>,
    ) => {
        return (
            <StyledComponent
                {...(props as Props)}
                forwardedAs={as}
                ref={ref}
                $minHeight={minHeight}
                $maxHeight={maxHeight}
                $minWidth={minWidth}
                $maxWidth={maxWidth}
                $center={center}
                $inline={inline}
                $flex={flex}
                $wide={wide}
                $mb={mb}
                $mt={mt}
                $ml={ml}
                $mr={mr}
                $m={m}
                $pb={pb}
                $pt={pt}
                $pl={pl}
                $pr={pr}
                $p={p}
            />
        );
    };

    if (typeof Component !== 'string') {
        const displayName = Component.displayName || Component.name || 'Component';
        BoxedComponent.displayName = `boxed(${displayName})`;
    }

    return forwardRef(BoxedComponent) as BoxedComponent<Props, As>;
};

export default boxed;
